import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, Phone, Mail, User, Building, Tag, Star, Zap, Calendar, TrendingUp, Share2, Users, LogOut, Settings, Briefcase, MoreVertical, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import analytics from '../utils/analytics';
import RatingStars from './RatingStars';
import ReviewModal from './ReviewModal';
import PlugModal from './PlugModal';
import ShareModal from './ShareModal';
import ReferralModal from './ReferralModal';
import ProfileModal from './ProfileModal';
import ServiceProviders from './ServiceProviders';
import RemoveConnectionModal from './RemoveConnectionModal';
import PlugRelationshipModal from './PlugRelationshipModal';
import ReviewDetailsModal from './ReviewDetailsModal';
import PhotoUpload from './PhotoUpload';
import FriendProfileViewModal from './FriendProfileViewModal';
import NotificationBell from './NotificationBell';
import PublicProfile from './PublicProfile';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MainApp() {
  const { user, logout, emailLinkProfileId, clearEmailLinkProfileId } = useAuth();
  
  // Set user for analytics
  useEffect(() => {
    if (user) {
      analytics.setUser(user.id, {
        name: user.name,
        email: user.email,
        subscription_plan: user.subscription_plan
      });
    }
  }, [user]);
  
  const [friends, setFriends] = useState([]);
  const [filteredFriends, setFilteredFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New modal states
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isPlugModalOpen, setIsPlugModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isPlugRelationshipModalOpen, setIsPlugRelationshipModalOpen] = useState(false);
  const [isReviewDetailsModalOpen, setIsReviewDetailsModalOpen] = useState(false);
  const [isFriendProfileViewOpen, setIsFriendProfileViewOpen] = useState(false);
  const [isOwnProfilePreviewOpen, setIsOwnProfilePreviewOpen] = useState(false); // Added for profile preview
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [selectedFriends, setSelectedFriends] = useState([]); // For bulk sharing
  const [friendStats, setFriendStats] = useState({});
  const [currentView, setCurrentView] = useState('friends'); // 'friends' or 'providers'
  const [newlyAddedFriend, setNewlyAddedFriend] = useState(null); // For triggering plug relationship modal
  const [selectedUserId, setSelectedUserId] = useState(null); // For public profile modal from email links

  // Handle email link profile ID from AuthContext
  useEffect(() => {
    if (emailLinkProfileId) {
      console.log('📧 Processing email link profile ID from context:', emailLinkProfileId);
      setSelectedUserId(emailLinkProfileId);
      // Clear the profile ID from context so it doesn't persist
      clearEmailLinkProfileId();
    }
  }, [emailLinkProfileId, clearEmailLinkProfileId]); // Run when email link profile ID changes

  useEffect(() => {
    if (user) {  // Only fetch when user is authenticated
      fetchFriends();
      fetchServices();
    }
  }, [user]);  // Depend on user instead of empty array

  useEffect(() => {
    filterFriends();
  }, [friends, searchQuery, selectedService]);

  // Smart onboarding: Only redirect on initial load, not when user manually selects "My Network"
  useEffect(() => {
    if (user && friends.length === 0 && currentView === 'friends' && !loading) {
      // Only redirect if it's the initial load (when loading just finished)
      const hasUserManuallySelectedTab = sessionStorage.getItem('userSelectedTab');
      if (!hasUserManuallySelectedTab) {
        console.log('🎯 New user detected - redirecting to Find Providers for onboarding');
        setCurrentView('providers');
      }
    }
  }, [friends, currentView, user, loading]);

  useEffect(() => {
    // Fetch stats for all friends
    friends.forEach(friend => {
      fetchFriendStats(friend.id);
    });
  }, [friends]);

  const fetchFriends = async () => {
    try {
      console.log('=== FETCH FRIENDS STARTED ===');
      const token = localStorage.getItem('access_token');
      console.log('Auth headers check:', axios.defaults.headers.common['Authorization'] ? 'Present' : 'Missing');
      console.log('Token from localStorage:', token ? 'Present' : 'Missing');
      
      const response = await axios.get(`${API_URL}/api/friends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status === 'success') {
        console.log('Friends data loaded:', response.data.friends);
        // Debug: Log first friend to see available fields
        if (response.data.friends.length > 0) {
          console.log('First friend object fields:', Object.keys(response.data.friends[0]));
          console.log('First friend complete object:', response.data.friends[0]);
        }
        
        // Try to get user profiles for friends to fetch portfolio images
        const friendsWithPhotos = await Promise.all(
          response.data.friends.map(async (friend) => {
            // Always try to find matching provider for portfolio images (not just when photo_url is missing)
            if (friend.email) {
              try {
                console.log(`=== DEBUGGING FRIEND: ${friend.name} ===`);
                console.log(`Friend email being searched: "${friend.email}"`);
                
                // Use the same endpoint as "Find Providers" to get complete data
                const userResponse = await axios.get(`${API_URL}/api/providers/search`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                console.log(`API returned ${userResponse.data.providers?.length || 0} providers`);
                
                if (userResponse.data.status === 'success' && userResponse.data.providers.length > 0) {
                  // Try email matching first
                  let matchingProvider = userResponse.data.providers.find(provider => {
                    const providerEmail = provider.contact_email || provider.email || '';
                    const friendEmail = friend.email || '';
                    
                    console.log(`Checking provider ${provider.name}: "${providerEmail}" vs friend "${friendEmail}"`);
                    
                    // Exact email match (case insensitive)
                    return providerEmail.toLowerCase() === friendEmail.toLowerCase();
                  });
                  
                  // If no email match, try name matching as fallback
                  if (!matchingProvider) {
                    console.log(`No email match found, trying name matching for ${friend.name}`);
                    matchingProvider = userResponse.data.providers.find(provider => {
                      const providerName = provider.name.toLowerCase();
                      const friendName = friend.name.toLowerCase();
                      
                      console.log(`Name check: "${providerName}" vs "${friendName}"`);
                      
                      // Check if names contain each other (partial matching)
                      return providerName.includes(friendName) || friendName.includes(providerName) ||
                             providerName === friendName;
                    });
                  }
                  
                  if (matchingProvider) {
                    console.log(`✅ FOUND MATCH: ${matchingProvider.name} for ${friend.name}`);
                    console.log(`Complete provider object:`, matchingProvider);
                    console.log(`Provider portfolio_images:`, matchingProvider.portfolio_images);
                    console.log(`Provider has ${(matchingProvider.portfolio_images || []).length} portfolio images`);
                    
                    return { 
                      ...friend, 
                      // Only update photo_url if friend doesn't have one
                      photo_url: friend.photo_url || matchingProvider.profile_picture,
                      portfolio_images: matchingProvider.portfolio_images || []
                    };
                  } else {
                    console.log(`❌ NO MATCH found for ${friend.name}`);
                  }
                }
              } catch (error) {
                console.log('Could not find user profile for friend:', friend.name, error);
              }
            }
            return friend;
          })
        );
        
        console.log('=== SETTING FRIENDS STATE ===');
        console.log('Friends to set:', friendsWithPhotos.length);
        setFriends(friendsWithPhotos);
        
        // Force a render by setting loading to false after friends are set
        setTimeout(() => {
          setLoading(false);
        }, 100);
        
        // Smart onboarding: Only redirect on initial load, not when user manually clicks "My Network"
        if (friendsWithPhotos.length === 0 && currentView === 'friends') {
          const hasUserManuallySelectedTab = sessionStorage.getItem('userSelectedTab');
          if (!hasUserManuallySelectedTab) {
            console.log('🚀 New user detected - redirecting to Find Providers for better onboarding');
            setCurrentView('providers');
          }
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching friends:', error);
      if (error.response?.status === 401) {
        logout();
      }
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services`);
      if (response.data.status === 'success') {
        setAllServices(response.data.services);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      if (error.response?.status === 401) {
        logout();
      }
    }
  };

  const fetchFriendStats = async (friendId) => {
    try {
      const response = await axios.get(`${API_URL}/api/friends/${friendId}/stats`);
      if (response.data.status === 'success') {
        setFriendStats(prev => ({
          ...prev,
          [friendId]: response.data.stats
        }));
      }
    } catch (error) {
      console.error('Error fetching friend stats:', error);
    }
  };



  const filterFriends = () => {
    let filtered = friends;

    if (searchQuery.trim()) {
      filtered = filtered.filter(friend =>
        friend.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        friend.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        friend.services.some(service => 
          service.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    if (selectedService) {
      filtered = filtered.filter(friend =>
        friend.services.some(service =>
          service.toLowerCase().includes(selectedService.toLowerCase())
        )
      );
      analytics.trackServiceFilter(selectedService);
    }

    setFilteredFriends(filtered);
    
    // Track search if there's a query
    if (searchQuery.trim()) {
      analytics.trackSearch(searchQuery, filtered.length);
    }
  };

  const handleReviewSubmit = async (reviewData) => {
    try {
      const response = await axios.post(`${API_URL}/api/reviews`, reviewData);
      if (response.data.status === 'success') {
        // Refresh friend stats
        fetchFriendStats(reviewData.friend_id);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  const handlePlugSubmit = async (plugData) => {
    try {
      const response = await axios.put(`${API_URL}/api/friends/${selectedFriend.id}/plug`, plugData);
      if (response.data.status === 'success') {
        // Refresh friend stats
        fetchFriendStats(selectedFriend.id);
      }
    } catch (error) {
      console.error('Error updating plug relationship:', error);
    }
  };

  const handleNewFriendPlugSubmit = async (plugData) => {
    try {
      if (!newlyAddedFriend) return;
      
      const response = await axios.put(`${API_URL}/api/friends/${newlyAddedFriend.id}/plug`, plugData);
      if (response.data.status === 'success') {
        // Refresh friend stats
        fetchFriendStats(newlyAddedFriend.id);
        // Clear the newly added friend
        setNewlyAddedFriend(null);
      }
    } catch (error) {
      console.error('Error updating plug relationship for new friend:', error);
    }
  };

  const openReviewModal = (friend) => {
    setSelectedFriend(friend);
    setIsReviewModalOpen(true);
  };

  const openPlugModal = (friend) => {
    setSelectedFriend(friend);
    setIsPlugModalOpen(true);
  };

  const openRemoveModal = (friend) => {
    setSelectedFriend(friend);
    setIsRemoveModalOpen(true);
  };

  const openReviewDetailsModal = (friend) => {
    setSelectedFriend(friend);
    setIsReviewDetailsModalOpen(true);
  };

  const openFriendProfileView = (friend) => {
    setSelectedFriend(friend);
    setIsFriendProfileViewOpen(true);
  };

  const handleRemoveConnection = async (friendId) => {
    try {
      const response = await axios.delete(`${API_URL}/api/friends/${friendId}`);
      if (response.data.status === 'success') {
        // Remove from local state
        setFriends(prev => prev.filter(friend => friend.id !== friendId));
        setFilteredFriends(prev => prev.filter(friend => friend.id !== friendId));
        
        // Show success message
        alert(response.data.message);
        
        // Refresh services list
        fetchServices();
      }
    } catch (error) {
      console.error('Error removing connection:', error);
      alert('Failed to remove connection. Please try again.');
    }
  };

  const handleShare = async (shareData) => {
    try {
      const response = await axios.post(`${API_URL}/api/share/friends`, shareData);
      return response.data;
    } catch (error) {
      console.error('Error sharing friends:', error);
      throw error;
    }
  };

  const handleGenerateReferral = async (referralData) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API_URL}/api/referral/generate`, referralData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error generating referral:', error);
      throw error;
    }
  };

  // Simple profile share function (alternative to complex referral system)
  const handleGenerateProfileShare = async (friendId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API_URL}/api/profile/share`, { friend_id: friendId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error generating profile share:', error);
      throw error;
    }
  };

  const openShareModal = () => {
    if (selectedFriends.length === 0) {
      alert('Please select friends to share first');
      return;
    }
    setIsShareModalOpen(true);
  };

  const openReferralModal = (friend) => {
    setSelectedFriend(friend);
    setIsReferralModalOpen(true);
  };

  const handleCall = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
      analytics.trackCall('friend_contact');
    }
  };

  const handleEmail = (email) => {
    if (email) {
      window.location.href = `mailto:${email}`;
      analytics.trackEmail('friend_contact');
    }
  };

  const getServiceColor = (service) => {
    const colors = [
      'hover:bg-blue-200',
      'hover:bg-green-200', 
      'hover:bg-purple-200',
      'hover:bg-orange-200',
      'hover:bg-pink-200',
      'hover:bg-indigo-200'
    ];
    const baseColors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800', 
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800',
      'bg-indigo-100 text-indigo-800'
    ];
    const index = service.length % colors.length;
    return `${baseColors[index]} ${colors[index]}`;
  };

  const formatPlugStartDate = (plugSince) => {
    if (!plugSince) return null;
    
    const startDate = new Date(plugSince);
    const options = { year: 'numeric', month: 'short' };
    return startDate.toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-xl" style={{color: 'var(--brand-navy)'}}>Loading your network...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-xl border-b border-slate-200/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* TrustPlug Logo */}
              <div className="flex items-center mr-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_pro-connector/artifacts/pkftxatb_TrustyPlug.PNG" 
                  alt="TrustPlug Logo" 
                  className="h-16 w-auto"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold" style={{color: 'var(--brand-navy)'}}>TrustPlug</h1>
                <p className="mt-1" style={{color: 'var(--brand-navy)'}} className="opacity-80">
                  Welcome back, {user?.name}!
                </p>
              </div>
            </div>
              
            <div className="flex items-center gap-3">
              {/* Navigation Tabs */}
              <div className="flex bg-white/50 rounded-lg p-1 mr-4">
                <Button
                  variant={currentView === 'friends' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    sessionStorage.setItem('userSelectedTab', 'friends');
                    setCurrentView('friends');
                  }}
                  className={currentView === 'friends' ? 'text-white' : ''}
                  style={currentView === 'friends' ? {
                    background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-dark) 100%)'
                  } : {}}
                >
                  <Users className="w-4 h-4 mr-2" />
                  My Network
                </Button>
                <Button
                  variant={currentView === 'providers' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    sessionStorage.setItem('userSelectedTab', 'providers');
                    setCurrentView('providers');
                  }}
                  className={currentView === 'providers' ? 'text-white' : ''}
                  style={currentView === 'providers' ? {
                    background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-dark) 100%)'
                  } : {}}
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Find Providers
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOwnProfilePreviewOpen(true)}
                  className="hover:bg-white/70"
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </Button>
              </div>
              
              {/* Notification Bell */}
              <NotificationBell />
              
              {/* User Profile & Actions */}
              <div className="flex items-center gap-3 mr-4">
                <div className="text-right">
                  <div className="text-sm font-medium" style={{color: 'var(--brand-navy)'}}>
                    {user?.name}
                  </div>
                  <div className="text-xs" style={{color: 'var(--brand-navy)'}} className="opacity-60">
                    {user?.subscription_plan ? user.subscription_plan.charAt(0).toUpperCase() + user.subscription_plan.slice(1) : 'Free'} Plan
                  </div>
                </div>
                <Button
                  onClick={() => setIsProfileModalOpen(true)}
                  variant="outline"
                  size="sm"
                  className="border-gray-300"
                >
                  <Settings className="w-4 h-4" />
                </Button>
                <Button
                  onClick={logout}
                  variant="outline"
                  size="sm"
                  className="border-gray-300"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Share Selected Button */}
              {selectedFriends.length > 0 && (
                <Button
                  onClick={openShareModal}
                  variant="outline"
                  className="border-2"
                  style={{
                    borderColor: 'var(--brand-tan)',
                    color: 'var(--brand-tan)'
                  }}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Selected ({selectedFriends.length})
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {currentView === 'friends' ? (
          <>
            {/* Search and Filter Section */}
            <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/50 mb-8">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search Input */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    placeholder="Search friends, companies, or services..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-12 text-lg"
                  />
                </div>

                {/* Service Filter */}
                <div className="md:w-64">
                  <select
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                    className="w-full h-12 px-4 border border-slate-200 rounded-lg bg-white/50 text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Services</option>
                    {allServices.map(service => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Friends Grid */}
            {filteredFriends.length === 0 ? (
              <div className="text-center py-16">
                <User className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-600 mb-2">
                  {friends.length === 0 ? "No friends added yet" : "No friends found"}
                </h3>
                <p className="text-slate-500 mb-4">
                  {friends.length === 0 
                    ? "Start building your network by finding trusted service providers in our directory!"
                    : "Try adjusting your search criteria"
                  }
                </p>
                {friends.length === 0 && (
                  <Button 
                    onClick={() => {
                      sessionStorage.setItem('userSelectedTab', 'providers');
                      setCurrentView('providers');
                    }}
                    className="text-white"
                    style={{
                      background: 'linear-gradient(135deg, var(--brand-tan) 0%, var(--brand-tan-dark) 100%)'
                    }}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Find Providers
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredFriends.map(friend => {
                  const stats = friendStats[friend.id] || {};
                  const plugStartDate = formatPlugStartDate(stats.plug_since);
                  
                  return (
                    <Card 
                      key={friend.id} 
                      className="group hover:shadow-xl transition-all duration-300 bg-white/70 backdrop-blur-sm border-slate-200/50 hover:-translate-y-1 cursor-pointer"
                      onClick={() => openFriendProfileView(friend)}
                    >
                      <CardHeader className="pb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {(friend.photo_url || friend.profile_picture) ? (
                              <img
                                src={(friend.photo_url || friend.profile_picture).startsWith('data:') ? (friend.photo_url || friend.profile_picture) : `${API_URL}${friend.photo_url || friend.profile_picture}`}
                                alt={friend.name}
                                className="w-12 h-12 rounded-full object-cover border-2"
                                style={{borderColor: 'var(--brand-navy)'}}
                                onError={(e) => {
                                  console.log('Image failed to load:', e.target.src);
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${(friend.photo_url || friend.profile_picture) ? 'hidden' : 'flex'}`}
                              style={{background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-light) 100%)'}}
                            >
                              {friend.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <CardTitle className="text-lg" style={{color: 'var(--brand-navy)'}}>
                                {friend.name}
                              </CardTitle>
                              {friend.company && (
                                <div className="flex items-center text-sm mt-1" style={{color: 'var(--brand-navy)'}} className="opacity-70">
                                  <Building className="w-4 h-4 mr-1" />
                                  {friend.company}
                                </div>
                              )}
                              
                              {/* Stats Row */}
                              <div className="flex items-center gap-3 mt-2">
                                {stats.average_rating > 0 && (
                                  <div 
                                    className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openReviewDetailsModal(friend);
                                    }}
                                  >
                                    <RatingStars rating={Math.floor(stats.average_rating)} size="w-3 h-3" />
                                    <span className="text-xs" style={{color: 'var(--brand-navy)'}} className="opacity-60">
                                      ({stats.total_reviews})
                                    </span>
                                  </div>
                                )}
                                
                                {plugStartDate && (
                                  <div className="flex items-center gap-1">
                                    <Zap className="w-3 h-3" style={{color: 'var(--brand-tan)'}} />
                                    <span className="text-xs font-medium" style={{color: 'var(--brand-tan)'}}>
                                      Since {plugStartDate}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent className="pt-0">
                        {/* Remove Connection Button - Moved down for better UX */}
                        <div className="flex justify-end mb-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openRemoveModal(friend);
                            }}
                            className="w-8 h-8 p-0 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Services */}
                        {friend.services && friend.services.length > 0 && (
                          <div className="mb-4">
                            <div className="flex items-center mb-2">
                              <Tag className="w-4 h-4 mr-1" style={{color: 'var(--brand-navy)'}} className="opacity-60" />
                              <span className="text-sm font-medium" style={{color: 'var(--brand-navy)'}}>Services</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {friend.services.map((service, index) => (
                                <Badge
                                  key={index}
                                  variant="secondary"
                                  className={`${getServiceColor(service)} cursor-pointer transition-colors duration-200`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedService(service);
                                  }}
                                >
                                  {service}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="space-y-3">
                          {/* Contact Actions */}
                          <div className="flex gap-2">
                            {friend.phone && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCall(friend.phone);
                                }}
                                className="flex-1 hover:bg-green-50 hover:border-green-200 hover:text-green-700"
                              >
                                <Phone className="w-4 h-4 mr-1" />
                                Call
                              </Button>
                            )}
                            {friend.email && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEmail(friend.email);
                                }}
                                className="flex-1 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700"
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                Email
                              </Button>
                            )}
                          </div>

                          {/* Review & Plug Actions */}
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openReviewModal(friend);
                              }}
                              className="w-full text-white border-0"
                              style={{
                                background: 'linear-gradient(135deg, var(--brand-tan) 0%, var(--brand-tan-dark) 100%)'
                              }}
                            >
                              <Star className="w-4 h-4 mr-1" />
                              Review
                            </Button>
                          </div>
                          
                          {/* Referral Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openReferralModal(friend);
                            }}
                            className="w-full"
                            style={{
                              borderColor: 'var(--brand-navy)',
                              color: 'var(--brand-navy)'
                            }}
                          >
                            <TrendingUp className="w-4 h-4 mr-1" />
                            Share Profile
                          </Button>
                        </div>

                        {/* Contact Info Display */}
                        {(friend.phone || friend.email) && (
                          <div className="mt-3 pt-3 border-t border-slate-200/50 text-sm space-y-1" style={{color: 'var(--brand-navy)'}} className="opacity-60">
                            {friend.phone && (
                              <div className="flex items-center">
                                <Phone className="w-3 h-3 mr-2" />
                                {friend.phone}
                              </div>
                            )}
                            {friend.email && (
                              <div className="flex items-center">
                                <Mail className="w-3 h-3 mr-2" />
                                {friend.email}
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          /* Service Providers View */
          <ServiceProviders />
        )}
      </div>
      
      {/* Remove Connection Modal */}
      <RemoveConnectionModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        friend={selectedFriend}
        onRemove={handleRemoveConnection}
      />
      
      {/* Profile Modal */}
      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />
      
      {/* Review Modal */}
      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        friend={selectedFriend}
        onSubmit={handleReviewSubmit}
      />
      
      {/* Plug Tracking Modal */}
      <PlugModal
        isOpen={isPlugModalOpen}
        onClose={() => setIsPlugModalOpen(false)}
        friend={selectedFriend}
        onSubmit={handlePlugSubmit}
      />
      
      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        selectedFriends={selectedFriends}
        onShare={handleShare}
      />
      
      {/* Referral Modal */}
      <ReferralModal
        isOpen={isReferralModalOpen}
        onClose={() => setIsReferralModalOpen(false)}
        friend={selectedFriend}
        onGenerateReferral={handleGenerateReferral}
        onGenerateProfileShare={handleGenerateProfileShare}
      />
      
      {/* Plug Relationship Modal - for newly added friends */}
      <PlugRelationshipModal
        isOpen={isPlugRelationshipModalOpen}
        onClose={() => {
          setIsPlugRelationshipModalOpen(false);
          setNewlyAddedFriend(null);
        }}
        friend={newlyAddedFriend}
        onSubmit={handleNewFriendPlugSubmit}
      />
      
      {/* Review Details Modal */}
      <ReviewDetailsModal
        isOpen={isReviewDetailsModalOpen}
        onClose={() => setIsReviewDetailsModalOpen(false)}
        friend={selectedFriend}
        onNewReview={handleReviewSubmit}
      />
      
      {/* Friend Profile View Modal */}
      <FriendProfileViewModal
        isOpen={isFriendProfileViewOpen}
        onClose={() => setIsFriendProfileViewOpen(false)}
        friend={selectedFriend}
        onReviewClick={openReviewModal}
        onPlugClick={openPlugModal}
        onRemoveClick={openRemoveModal}
        onReferralClick={openReferralModal}
      />
      
      {/* Own Profile Preview Modal */}
      {user && isOwnProfilePreviewOpen && (
        <PublicProfile
          userId={user.id}
          isModal={true}
          onClose={() => setIsOwnProfilePreviewOpen(false)}
        />
      )}
      
      {/* Email Link Profile Modal */}
      {selectedUserId && (
        <PublicProfile
          userId={selectedUserId}
          isModal={true}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
}

export default MainApp;